import { useEffect, useRef, useState } from "react";

// LIBRARIES
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// ASSETS
import PauseIcon from "assets/img/Icon awesome-pause.svg";
import PlayIcon from "assets/img/Icon awesome-play.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import VideoPlaceholder from "assets/img/VideoPlaceholder.png";

// STYLES
import {
  BgOverlay,
  Body,
  Bullet,
  ButtonContainer,
  CarouselContainer,
  CircularProgressBarContainer,
  Footer,
  Header,
  ImageBackground,
  InfoContainer,
  LeftSideWrapper,
  Line,
  PlayButton,
  PlayImage,
  TitleContainer,
  VideoBackground,
  VideoPreview,
} from "./HeroCarouselStyled";

// MISC
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import { IProps } from "./HeroCarouselModel";
import useWindowWidth from "hooks/useWindowWidth";
import { appRoutes } from "config/AppRoutesConfig";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import CustomText from "components/Atoms/CustomText/CustomText";
import mapService from "services/mapService";
import { secondUploadBaseUrl } from "services/helperBase";

// CONFIGURATION

const HeroCarousel = (props: IProps) => {
  // PROPERTIES
  const { isHomePage = false } = props;

  // LIBRARY CONSTANTS

  const { t } = useTranslation();
  const navigate = useNavigate();

  // STATE CONSTANTS
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const windowWidth = useWindowWidth();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const isCampaignsLoaded = campaigns && campaigns.length > 0;
  const currentCampaign = isCampaignsLoaded ? campaigns[currentIndex] : null;
  // LIFE CYCLE

  useEffect(() => {
    if (isCampaignsLoaded && currentIndex >= campaigns?.length) {
      setCurrentIndex(0); // Reset index if out of bounds
    }
  }, [currentIndex, isCampaignsLoaded, campaigns]);

  useEffect(() => {
    if (isPlaying && isCampaignsLoaded) {
      const timer = setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % campaigns.length);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, isPlaying, isCampaignsLoaded, campaigns.length]);

  useEffect(() => {
    if (videoRef.current && isCampaignsLoaded) {
      if (isPlaying) {
        videoRef.current.play();
        const interval = setInterval(() => {
          const video = videoRef.current;
          if (video) {
            const currentTime = video.currentTime;
            const duration = 10;
            setProgress((currentTime / duration) * 100);
          }
        }, 100);
        return () => clearInterval(interval);
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, currentIndex, isCampaignsLoaded]);

  useEffect(() => {
    const pauseTimer = setTimeout(() => {
      setIsPlaying(false);
    }, 120000);

    return () => clearTimeout(pauseTimer);
  }, [isPlaying]);

  // EVENT HANDLERS
  const handleBulletClick = (index: number) => {
    // console.log("index", index);
    setCurrentIndex(index);
  };

  const handleStartPauseClick = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    mapService
      .getCampaigns()
      .then((response) => {
        const tmpCampaigns = response.data
          .map((campaign: any) => {
            const isVideo =
              campaign.attributes.cover.data.attributes.mime.includes("video");
            return {
              id: campaign?.id,
              date: campaign?.attributes?.start_date
                .split("-")
                .reverse()
                .join("."), // keep for display
              rawDate: campaign?.attributes?.start_date, // keep raw ISO for sorting
              title: campaign?.attributes?.title,
              headerTitle: "Campanie " + campaign?.id,
              text: campaign?.attributes.content[0].children[0].text,
              mediaUrl: `${secondUploadBaseUrl}${campaign.attributes.cover.data.attributes.url}`,
              isVideo,
              onCardClick: () => navigate(`/campanii/${campaign.id}`),
            };
          })
          // ✅ newest → oldest
          .sort((a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime());
  
        setCampaigns(tmpCampaigns);
      })
      .catch((err) => {
        console.log("getCampaigns error", err);
      });
  }, [navigate]);
  if (campaigns)
    return (
      <CarouselContainer isHomePage={isHomePage}>
        <BgOverlay />
        {currentCampaign?.isVideo ? (
          <VideoBackground
            ref={videoRef}
            src={
              currentCampaign?.mediaUrl?.trim()
                ? currentCampaign.mediaUrl
                : VideoPlaceholder
            }
            autoPlay
            muted
            loop
            preload="metadata"
            playsInline
          />
        ) : (
          <ImageBackground
            src={
              currentCampaign?.mediaUrl?.trim()
                ? currentCampaign.mediaUrl
                : CoverPlaceholder
            }
          />
        )}
        <Header>
          <CustomText
            type={TextType.P2}
            text={currentCampaign?.date || ""}
            color={COLORS.Background}
          />
        </Header>
        <Body>
          <TitleContainer>
            <CustomText
              type={TextType.H2}
              text={currentCampaign?.title || ""}
              color={COLORS.Background}
            />
          </TitleContainer>
          <InfoContainer>
            {(windowWidth > 600 || isHomePage) && (
              <CustomText
                type={TextType.P2}
                text={currentCampaign?.text || ""}
                color={COLORS.Background}
              />
            )}
          </InfoContainer>
          <ButtonContainer>
            <ButtonAtom
              text={
                !isHomePage && windowWidth < 600
                  ? t("view.homePage.openArchive")
                  : t("view.homePage.open")
              }
              type={ButtonType.ArrowButton}
              rightIcon={true}
              onClick={() => currentCampaign?.onCardClick()}
            />
          </ButtonContainer>
          <Line />
        </Body>
        <Footer>
          <LeftSideWrapper>
            {campaigns?.map((video, index) => (
              <VideoPreview key={index}>
                {(currentIndex === index || true) && (
                  <CircularProgressBarContainer>
                    <CircularProgressbar
                      value={progress}
                      styles={buildStyles({
                        rotation: 0.5,
                        pathTransition:
                          progress === 0
                            ? "none"
                            : "stroke-dashoffset 0.5s ease 0s",
                        pathColor: COLORS.Yellow,
                        trailColor: "transparent",
                      })}
                    />
                  </CircularProgressBarContainer>
                )}
                <Bullet
                  src={video.mediaUrl}
                  key={index}
                  onClick={() => handleBulletClick(index)}
                />
              </VideoPreview>
            ))}
            {currentCampaign?.isVideo && (
              <PlayButton onClick={handleStartPauseClick}>
                <PlayImage src={isPlaying ? PauseIcon : PlayIcon} alt="play" />
              </PlayButton>
            )}
          </LeftSideWrapper>
          {!isHomePage && windowWidth > 600 && (
            <ButtonAtom
              text={t("view.homePage.openArchive")}
              type={ButtonType.ArrowButton}
              rightIcon={true}
              onClick={() => navigate(appRoutes.campaignArchive)}
            />
          )}
        </Footer>
      </CarouselContainer>
    );
};

export default HeroCarousel;
